import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Send, Loader2, Check, X, Copy, ExternalLink, Eye, Users, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * Bouton + dialog pour présenter 2-3 talents (anonymisés) à un client.
 * Génère un lien public sécurisé `/client/talents/{token}`.
 */
const PresentTalentsCard = ({ clientId, missionId = null }) => {
  const [open, setOpen] = useState(false);
  const [pool, setPool] = useState([]);
  const [loadingPool, setLoadingPool] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [overrides, setOverrides] = useState({}); // { talent_id: { alias, price_estimate_eur } }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, '');

  const loadPool = async () => {
    setLoadingPool(true);
    try {
      const res = await api.get('/admin/talent-pool?limit=100');
      const data = await res.json();
      setPool(data.employees || data.candidates || data.items || []);
    } catch (e) {
      toast.error(`Erreur chargement vivier : ${e.message}`);
    } finally {
      setLoadingPool(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/admin/clients/${clientId}/presentations`);
      const data = await res.json();
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { if (clientId) loadHistory(); }, [clientId]);

  const openDialog = () => {
    setSelectedIds([]);
    setOverrides({});
    setNotes('');
    setCreatedLink(null);
    setOpen(true);
    loadPool();
  };

  const toggle = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 5 ? prev : [...prev, id]));
  };

  const setOverride = (id, key, value) => {
    setOverrides((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const submit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      const cleanedOverrides = {};
      Object.entries(overrides).forEach(([tid, ov]) => {
        if (!selectedIds.includes(tid)) return;
        const entry = {};
        if (ov.alias?.trim()) entry.alias = ov.alias.trim();
        if (ov.price_estimate_eur && !Number.isNaN(Number(ov.price_estimate_eur))) {
          entry.price_estimate_eur = Number(ov.price_estimate_eur);
        }
        if (Object.keys(entry).length > 0) cleanedOverrides[tid] = entry;
      });
      const body = {
        talent_ids: selectedIds,
        notes: notes || null,
        mission_id: missionId,
        talent_overrides: Object.keys(cleanedOverrides).length > 0 ? cleanedOverrides : null,
      };
      const res = await api.post(`/admin/clients/${clientId}/presentations`, body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const fullUrl = `${window.location.origin}${data.portal_path}`;
      setCreatedLink(fullUrl);
      toast.success('Lien créé. À envoyer au client.');
      loadHistory();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const revoke = async (presId) => {
    if (!window.confirm('Révoquer ce lien ? Le client ne pourra plus y accéder.')) return;
    try {
      const res = await api.post(`/admin/clients/presentations/${presId}/revoke`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Lien révoqué');
      loadHistory();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    }
  };

  const filteredPool = pool;

  return (
    <>
      <Card className="p-4 md:p-5" data-testid="present-talents-card">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-base font-display font-semibold flex items-center gap-2">
            <Send className="h-4 w-4 text-tp-primary" />
            Présenter des talents
          </h3>
          <Button onClick={openDialog} size="sm" className="bg-tp-primary hover:bg-tp-primary-light text-white" data-testid="open-present-dialog-btn">
            <Sparkles className="h-4 w-4 mr-1.5" />
            Nouvelle présentation
          </Button>
        </div>

        {loadingHistory ? (
          <p className="text-xs text-tp-text-light"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Chargement...</p>
        ) : history.length === 0 ? (
          <p className="text-xs text-tp-text-light">Aucune présentation envoyée. Sélectionne 2-3 talents pour démarrer le closing.</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 5).map((p) => {
              const url = `${window.location.origin}/client/talents/${p.token}`;
              const decided = p.decisions.filter(d => d.decision).length;
              const isRevoked = p.status === 'revoked';
              return (
                <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 rounded border border-tp-border bg-white text-xs" data-testid={`presentation-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-tp-text">{p.talents_count} talent(s)</span>
                      {p.viewed_at ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          <Eye className="h-2.5 w-2.5 mr-1" /> Vu {p.view_count}×
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Pas encore vu</Badge>
                      )}
                      {decided > 0 && (
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                          {decided} décision(s)
                        </Badge>
                      )}
                      {isRevoked && <Badge className="bg-red-50 text-red-700 text-[10px]">Révoqué</Badge>}
                    </div>
                    <p className="text-[10px] text-tp-text-light mt-0.5 truncate">
                      Créé {new Date(p.created_at).toLocaleDateString('fr-FR')} · expire {new Date(p.expires_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => copyLink(url)} className="p-1.5 rounded hover:bg-tp-bg" title="Copier" data-testid={`copy-link-${p.id}`}>
                      <Copy className="h-3 w-3" />
                    </button>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-tp-bg" title="Ouvrir">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    {!isRevoked && (
                      <button onClick={() => revoke(p.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Révoquer" data-testid={`revoke-${p.id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="present-talents-dialog">
          <DialogHeader>
            <DialogTitle>Présenter des talents au client</DialogTitle>
            <DialogDescription>
              Sélectionne 2-5 talents du Talent Pool. Le client verra une page anonymisée
              (alias + score + résumé IA + tarif). Aucun email/téléphone/CV brut ne sera exposé.
            </DialogDescription>
          </DialogHeader>

          {createdLink ? (
            <div className="space-y-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-center">
                <Check className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-900">Présentation créée</p>
                <p className="text-xs text-emerald-700 mt-1">Le lien ci-dessous est sécurisé et valide 14 jours.</p>
              </div>
              <div className="flex items-center gap-2">
                <Input value={createdLink} readOnly className="font-mono text-xs" data-testid="created-link-input" />
                <Button onClick={() => copyLink(createdLink)} variant="outline" data-testid="created-link-copy-btn">
                  <Copy className="h-4 w-4 mr-1" />Copier
                </Button>
              </div>
              <div className="text-xs text-tp-text-light bg-tp-bg/50 rounded p-3">
                <p className="font-medium mb-1">À envoyer au client par email :</p>
                <p>« Voici la sélection que nous avons préparée pour vous : <span className="text-tp-primary">{createdLink}</span> »</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Talent picker */}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Talents (sélectionnés : {selectedIds.length}/5)
                </p>
                {loadingPool ? (
                  <div className="text-xs text-tp-text-light"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Chargement vivier...</div>
                ) : filteredPool.length === 0 ? (
                  <p className="text-xs text-tp-text-light italic">Aucun talent dans le pool. Clique "Garder" sur des candidats pour les y faire entrer.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1 border border-tp-border rounded-md p-2">
                    {filteredPool.map((c) => {
                      const id = c._id || c.id;
                      const checked = selectedIds.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggle(id)}
                          className={`text-left rounded-md p-2 border-2 transition ${checked ? 'border-tp-primary bg-tp-primary/5' : 'border-tp-border hover:border-tp-primary/40'}`}
                          data-testid={`pool-pick-${id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-tp-text truncate">
                                {c.first_name || 'Talent'} {c.last_name?.[0] || ''}.
                              </p>
                              <p className="text-xs text-tp-text-light truncate">
                                {c.main_profession || c.primary_category || '—'} · {c.country || '—'}
                              </p>
                              {c.qualification_score && (
                                <p className="text-[10px] text-tp-primary font-medium mt-0.5">Score {c.qualification_score}/100</p>
                              )}
                            </div>
                            {checked && <Check className="h-4 w-4 text-tp-primary flex-shrink-0 ml-2" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Overrides per selected talent */}
              {selectedIds.length > 0 && (
                <div className="space-y-2 border-t border-tp-border pt-3">
                  <p className="text-sm font-medium">Personnaliser (optionnel)</p>
                  {selectedIds.map((tid) => {
                    const c = pool.find(x => (x._id || x.id) === tid);
                    return (
                      <div key={`ov-${tid}`} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center bg-tp-bg/30 p-2 rounded">
                        <div className="text-xs text-tp-text font-medium truncate">
                          {c?.first_name || 'Talent'} {c?.last_name?.[0] || ''}.
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Alias (ex: Lucas A.)"
                            value={overrides[tid]?.alias || ''}
                            onChange={(e) => setOverride(tid, 'alias', e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`alias-${tid}`}
                          />
                          <Input
                            type="number"
                            placeholder="Tarif EUR/mois"
                            value={overrides[tid]?.price_estimate_eur || ''}
                            onChange={(e) => setOverride(tid, 'price_estimate_eur', e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`price-${tid}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notes */}
              <div>
                <p className="text-sm font-medium mb-1.5">Note pour le client (optionnelle)</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex: Voici 3 profils en lien avec votre besoin de Community Manager..."
                  rows={2}
                  data-testid="presentation-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {createdLink ? (
              <Button onClick={() => setOpen(false)} data-testid="close-after-create-btn">Fermer</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Annuler</Button>
                <Button
                  onClick={submit}
                  disabled={submitting || selectedIds.length === 0}
                  className="bg-tp-primary hover:bg-tp-primary-light text-white"
                  data-testid="submit-presentation-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Créer le lien
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PresentTalentsCard;
